'use client'
import { type HubConnection, type HubConnectionState } from '@microsoft/signalr'
import { Provider, atom } from 'jotai'
import { splitAtom } from 'jotai/utils'
import type { Map as LeafLetMap } from 'leaflet'
import type { Theme } from '@/actions/ui'
import type { UnknownAlarm } from '@/models/types/alarm/unknown-alarm'
import type { PhotovoltaicPlant } from '@/models/types/photovoltaic/photovoltaic-plant'
import type { BasePLC } from '@/models/types/plc/base-plc'
import type { DigitalSensor } from '@/models/types/plc/digital-sensor'
import type { DynamicSensor } from '@/models/types/plc/dynamic-sensor'
import type { NewPhotoConfiguration, PhotoConfiguration } from '@/models/types/plc/photo-configuration'
import type { PLC } from '@/models/types/plc/plc'
import type { PLCConfiguration } from '@/models/types/plc/plc-configuration'
import type { SeismicConfiguration } from '@/models/types/plc/seismic-configuration'
import type { StaticSensor } from '@/models/types/plc/static-sensor'
import type { Bms } from '@/models/types/bms/bms'
import type { BmsSensor } from '@/models/types/bms/bms-sensor'
import { AtomsHydrator } from '../atoms-hydrator'

//UI SIDE MENU
const sideMenuOpenAtom = atom(true)
const loadingOverlayAtom = atom<{ visible?: boolean; message?: string }>({ visible: false, message: '' })

//UI OTHER
const themeAtom = atom<Theme>('system')
const dirtyFormAtom = atom(false)

//PLC
const basePlcAtom = atom<BasePLC>()
const currentPlcAtom = atom<PLC>()
const facilityIdAtom = atom<number>()
const plcConfigurationAtom = atom<PLCConfiguration>()
const seismicConfigurationAtom = atom<SeismicConfiguration>()
const plcPhotoConfigurationsAtom = atom<(PhotoConfiguration | NewPhotoConfiguration)[]>([])
const plcDynamicSensorsAtom = atom<DynamicSensor[]>([])
const plcDynamicSensorsAtomsAtom = splitAtom(plcDynamicSensorsAtom)
const plcStaticSensorsAtom = atom<StaticSensor[]>([])
const plcStaticSensorsAtomsAtom = splitAtom(plcStaticSensorsAtom)
const plcDigitalSensorsAtom = atom<DigitalSensor[]>([])
const plcDigitalSensorsAtomsAtom = splitAtom(plcDigitalSensorsAtom)
const plcDefaultSensorsAtom = atom<number[]>([])

//PHOTOVOLTAIC
const photovoltaicPlantAtom = atom<PhotovoltaicPlant>()
const photovoltaicFormDirtyAtom = atom(false)

// BMS
const bmsAtom = atom<Bms | undefined>(undefined)
const bmsSensorsAtom = atom<BmsSensor[]>([])

//SIGNAL R
const hubConnection = atom<HubConnection>()

//ALARMS
const realtimeAlarmsAtom = atom<UnknownAlarm[]>([])

//MAP
const mapAtom = atom<LeafLetMap>()
const fullScreenAtom = atom(false)

//SIGNALR
const hubConnectionAtom = atom<HubConnection>()
const hubConnectionStateAtom = atom<HubConnectionState>()

type AtomsProviderProps = {
    children: JSX.Element
    themeInitalState: Theme
}

function AtomsProvider(props: AtomsProviderProps): JSX.Element {
    const { children, themeInitalState } = props
    return (
        <Provider>
            <AtomsHydrator atomValues={[[themeAtom, themeInitalState]]}>{children}</AtomsHydrator>
        </Provider>
    )
}

export {
    AtomsProvider,
    basePlcAtom,
    currentPlcAtom,
    dirtyFormAtom,
    facilityIdAtom,
    fullScreenAtom,
    hubConnection,
    hubConnectionAtom,
    hubConnectionStateAtom,
    loadingOverlayAtom,
    mapAtom,
    photovoltaicFormDirtyAtom,
    photovoltaicPlantAtom,
    plcConfigurationAtom,
    plcDefaultSensorsAtom,
    plcDigitalSensorsAtom,
    plcDigitalSensorsAtomsAtom,
    plcDynamicSensorsAtom,
    plcDynamicSensorsAtomsAtom,
    plcPhotoConfigurationsAtom,
    plcStaticSensorsAtom,
    plcStaticSensorsAtomsAtom,
    realtimeAlarmsAtom,
    seismicConfigurationAtom,
    sideMenuOpenAtom,
    themeAtom,
    bmsAtom,
    bmsSensorsAtom,
    
}
